/**
 * @component ry-fields
 * @category  form
 * @summary   Horizontal row of `<ry-field>` children. CSS-only flex container that honors each child's `[width="1..16"]` attribute (Fomantic's `.one.wide.field`, etc.). Set `[equal]` to make all children share equal width.
 *
 * @attribute equal     boolean   Make all child fields share width equally. Overrides each child's `[width]`.
 * @attribute grouped   boolean   Stack children vertically (used for grouped checkboxes / radios — Fomantic `.grouped.fields`).
 * @attribute inline    boolean   Lay out children inline with no per-field margin between them (Fomantic `.inline.fields`).
 *
 * @content   One or more `<ry-field>` children. Non-field children are still laid out but get no special width handling.
 *
 * @dom
 *   ry-fields [equal?] [grouped?] [inline?]
 *     > ry-field width="3"  ...
 *     > ry-field width="3"  ...
 *     > ry-field width="..."  ...
 *
 * @gotcha  `[width]` on a child only takes effect when its parent is `<ry-fields>` (flex row context). Outside of `<ry-fields>` the field is block-level.
 *
 * @gotcha  Grouped + inline are mutually exclusive — grouped wins.
 *
 * @example two-column
 * ```html
 * <ry-fields>
 *   <ry-field label="First name" width="8"><input type="text"></ry-field>
 *   <ry-field label="Last name"  width="8"><input type="text"></ry-field>
 * </ry-fields>
 * ```
 *
 * @example equal-width
 * ```html
 * <ry-fields equal>
 *   <ry-field label="Street"><input type="text"></ry-field>
 *   <ry-field label="City"><input type="text"></ry-field>
 *   <ry-field label="Zip"><input type="text"></ry-field>
 * </ry-fields>
 * ```
 *
 * @example three-uneven
 * ```html
 * <ry-fields>
 *   <ry-field label="Card"   width="10"><input type="text"></ry-field>
 *   <ry-field label="CVV"    width="3"><input type="text"></ry-field>
 *   <ry-field label="Exp"    width="3"><input type="text"></ry-field>
 * </ry-fields>
 * ```
 *
 * @example grouped-checkboxes
 * ```html
 * <ry-fields grouped>
 *   <ry-field label="Pick options"></ry-field>
 *   <ry-checkbox>Email me</ry-checkbox>
 *   <ry-checkbox>Text me</ry-checkbox>
 *   <ry-checkbox>Mail me</ry-checkbox>
 * </ry-fields>
 * ```
 *
 * @tutorial flex-row-mounted
 * ```js
 * const el = mount('<ry-fields><ry-field label="a"><input></ry-field><ry-field label="b"><input></ry-field></ry-fields>');
 * await expectEqual(el.children.length, 2);
 * ```
 *
 * @see ry-field, ry-form
 */

import { RyElement } from '../core/ry-element.js';

export class RyFields extends RyElement {
  setup(): void {
    this.setAttribute('role', 'group');
  }
}

customElements.define('ry-fields', RyFields);
